/* eslint-disable no-nested-ternary */
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import { faFloppyDisk } from '@fortawesome/free-solid-svg-icons/faFloppyDisk';
import isArray from 'lodash-es/isArray';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Images } from '../../../assets';
import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { atiraLinkTestIDs } from '../../../constants/test/atiralink.testIds';
import i18n, { AppLangs } from '../../../i18n';
import { AtiraLink } from '../../../model/atira-link/AtiraLink';
import { CreateAtiraLinkDto } from '../../../model/atira-link/dto/CreateAtiraLinkDto';
import { EditAtiraLinkDto } from '../../../model/atira-link/dto/EditAtiraLinkDto';
import { AtiraLinkPlanEmbed } from '../../../model/plan/types/AtiraLinkPlanEmbed.enum';
import { AtiraLinkPlanType } from '../../../model/plan/types/AtiraLinkPlanType.enum';
import { AtiraLinkLang } from '../../../model/settings/types/AtiraLinkLang.enum';
import { atiraLinkSliceSelectors } from '../../../redux/atira-link/atira-link.selector';
import { AtiraLinkActions } from '../../../redux/atira-link/atira-link.slice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { userSliceSelectors } from '../../../redux/user/user.selector';
import { Breakpoints } from '../../../theme/Breakpoints';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';
import { getUserLinkUrl } from '../../../utils/AtiraLink';
import { AtiraToast } from '../../../utils/AtiraToast';
import { getTypedCRUDErrors } from '../../../utils/getTypedCRUDErrors';
import { Tooltip } from '../../../utils/local-antd';
import { onSetTypedCRUDErrors } from '../../../utils/onSetTypedCRUDErrors';
import { TemplatePreviewMode } from '../../shared/model/TemplatePreviewMode.enum';
import { getEJSWrapperWidthHeight } from '../utils/getEJSWrapperWidthHeight';
import { AtiraLinkPreviewPhoneEJS } from './AtiraLinkPreviewPhoneEJS';

const Container = styled(Flex)<{
  isPreviewMode: boolean;
  previewMode: TemplatePreviewMode;
}>`
  flex-direction: column;
  align-items: center;
  position: sticky;
  top: ${Spacing.m};
  margin-inline: ${({ isPreviewMode }) => (!isPreviewMode ? Spacing.s : '')};
  overflow: hidden;

  ${({ isPreviewMode }) =>
    isPreviewMode
      ? `
    @media (max-width: ${Breakpoints.TABLET}) {      
      .atira-link-body {
        min-height: 70vh;
      }
    }
  `
      : ``}
`;

const PhoneWrapper = styled(Flex)<{
  isPreviewMode: boolean;
  previewMode: TemplatePreviewMode;
}>`
  position: relative;
  flex-direction: column;
  background-color: transparent;
  overflow: hidden;
  display: ${({ isPreviewMode }) => (isPreviewMode ? 'flex' : 'none')};

  @media (min-width: ${Breakpoints.DESKTOP}) {
    display: flex;
  }
`;

const ButtonsWrapper = styled(Flex)`
  position: fixed;
  bottom: 0;
  right: ${i18n.language === AppLangs.AR ? '50px' : '0'};
  left: ${i18n.language === AppLangs.AR ? '0' : '50px'};
  background-color: ${({ theme }) => theme.bgCanvas};
  z-index: 100;
  justify-content: center;
  align-items: center;
  padding: ${Spacing.s};
  gap: ${Spacing.s};
  width: calc(100% - 50px);
  box-shadow: 0px 0px 7px rgba(50, 50, 50, 0.75);

  @media (min-width: ${Breakpoints.DESKTOP}) {
    position: static;
    background-color: ${({ theme }) => theme.transparent};
    box-shadow: none;
    width: 90%;
  }
`;

const StyledButton = styled(Button)`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.s};
  border-radius: ${Rounded.pill};
  height: 2.4rem;
  padding: ${Spacing.m} !important;

  &:hover {
    background-color: ${({ theme }) => theme.mainHover};
  }

  @media (max-width: ${Breakpoints.DESKTOP}) {
    padding: ${Spacing.m};
    width: 12rem;

    span {
      display: inline-block;
    }
  }
`;

const PhoneFrameWrapper = styled(Flex)<{
  previewMode: TemplatePreviewMode;
  isPreviewMode: boolean;
  customBackgroundColor?: string;
}>`
  position: relative;
  z-index: 1;
  width: ${({ previewMode, isPreviewMode }) =>
    isPreviewMode
      ? previewMode === TemplatePreviewMode.MOBILE
        ? '230px'
        : previewMode === TemplatePreviewMode.TABLET
          ? '650px'
          : '1100px'
      : '230px'};
  aspect-ratio: ${({ previewMode }) =>
    previewMode === TemplatePreviewMode.MOBILE
      ? '  59 / 125'
      : previewMode === TemplatePreviewMode.TABLET
        ? '  475 / 363'
        : '2489 / 1421'};
  overflow: hidden;

  &::after {
    content: '';
    background-image: ${({ previewMode }) =>
      previewMode === TemplatePreviewMode.MOBILE
        ? `url(${Images.PreviewMobile})`
        : previewMode === TemplatePreviewMode.TABLET
          ? `url(${Images.PreviewIpad})`
          : `url(${Images.PreviewDesktop})`};
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    z-index: 1025;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  .ejs-wrapper {
    overflow: hidden;
    width: ${({ previewMode }) => getEJSWrapperWidthHeight(previewMode).width};
    height: ${({ previewMode }) =>
      getEJSWrapperWidthHeight(previewMode).height} !important;
    margin-inline: auto;
    margin-block: ${({ previewMode }) =>
      previewMode !== TemplatePreviewMode.DESKTOP ? 'auto' : '0'};

    ${({ previewMode }) =>
      previewMode === TemplatePreviewMode.DESKTOP
        ? `
        margin-top: 33px;
      `
        : ''}

    ${({ previewMode }) =>
      previewMode === TemplatePreviewMode.MOBILE
        ? `
          border-radius: 8px;
        `
        : ''}
  }
`;

interface AtiraLinkPreviewPhoneProps {
  values: (
    | CreateAtiraLinkDto
    | EditAtiraLinkDto
    | AtiraLink
    | Partial<AtiraLink>
  ) & {
    linkName: string;
  };
  currentLinkLang?: AtiraLinkLang;
  loading?: boolean;
  setLoading?: ATVoidFunction<boolean>;
  showActions?: boolean;
  isPreviewMode?: boolean;
  previewMode?: TemplatePreviewMode;
}

export const AtiraLinkPreviewPhoneWithContext: React.FC<
  AtiraLinkPreviewPhoneProps
> = ({
  values,
  currentLinkLang,
  loading,
  setLoading,
  showActions = false,
  isPreviewMode = false,
  previewMode = TemplatePreviewMode.MOBILE,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const {
    setError,
    clearErrors,
    formState: { errors: formErrors },
    reset,
    handleSubmit,
    watch,
  } = useFormContext<CreateAtiraLinkDto | EditAtiraLinkDto>();
  const selectedAtiraLink = useAppSelector(
    atiraLinkSliceSelectors.selectSelectedAtiraLink,
  );

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const editMode = Boolean(selectedAtiraLink?._id);

  const inputs = {
    linkName: document.querySelector(
      `[data-testid="${atiraLinkTestIDs.content.linkName}"]`,
    ),
    title: document.querySelector(
      `[data-testid="${atiraLinkTestIDs.content.title}"]`,
    ),
    bio: document.querySelector(
      `[data-testid="${atiraLinkTestIDs.content.bio}"]`,
    ),
    'phone.value': document.querySelector(
      `[data-testid="${atiraLinkTestIDs.content.phone.value}"]`,
    ),
    'email.value': document.querySelector(
      `[data-testid="${atiraLinkTestIDs.content.email.value}"]`,
    ),
    'website.value': document.querySelector(
      `[data-testid="${atiraLinkTestIDs.content.website.value}"]`,
    ),
    'map.value': document.querySelector(
      `[data-testid="${atiraLinkTestIDs.content.map.value}"]`,
    ),
    embed: document.querySelector(
      `[data-testid="${atiraLinkTestIDs.content.embed}"]`,
    ),
    socials:
      values?.socials?.map((social, index) => ({
        [social.handle]: document.getElementById(`social-${social.handle}`),
      })) || [],
    customLinks:
      values?.customLinks?.map((customLink, index) => ({
        [customLink.label]: document.getElementById(
          `custom-link-${customLink.label}-${customLink.value}`,
        ),
      })) || [],
  };

  const onScrollToFirstError = () => {
    Object.entries(formErrors || {}).forEach(([key, value]) => {
      const inputKey = ['phone', 'email', 'website', 'map'].includes(key)
        ? `${key}.value`
        : key;

      const input = inputs?.[inputKey as keyof typeof inputs];

      if (!input) {
        return;
      }

      if (!isArray(input)) {
        input.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      } else {
        const firstElement = input.find((elObj) => {
          const element = Object.values(elObj)[0];
          return element;
        });

        const element = firstElement && Object.values(firstElement)[0];
        element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });
  };

  const onSetErrors = (e: any) => {
    if (!pathname.includes('/my-link')) {
      return;
    }

    const errors = getTypedCRUDErrors(e);

    if (!errors?.length) {
      return;
    }

    const { field, message, value } = errors[0];

    if (field === 'socials') {
      values?.socials?.forEach((social, index) => {
        const isError = social?.handle?.toLowerCase() === value?.toLowerCase();

        if (isError) {
          setError(
            `socials.${index}.url`,
            {
              type: 'custom',
              message: t(message),
            },
            { shouldFocus: true },
          );
        }
      });
    } else if (field === 'customLinks') {
      values?.customLinks?.forEach((link, index) => {
        const isLabelError =
          link?.label?.toLowerCase() === value?.toLowerCase();
        const isValueError =
          link?.value?.toLowerCase() === value?.toLowerCase();

        if (isLabelError || isValueError) {
          setError(
            `customLinks.${index}.${isLabelError ? 'label' : 'value'}`,
            {
              type: 'custom',
              message: t(message),
            },
            { shouldFocus: true },
          );
        }
      });
    } else {
      onSetTypedCRUDErrors(e, setError);
    }

    Object.entries(values || {}).forEach(([key, value]) => {
      if (errors[0].field === 'socials') {
        values?.socials?.forEach((s, i) => {
          if (errors[0].value !== s.handle) {
            clearErrors(`socials.${i}.url` as keyof CreateAtiraLinkDto);
          }
        });
      }

      if (errors[0].field === 'customLinks') {
        values?.customLinks?.forEach((s, i) => {
          if (errors[0].value !== s.value && errors[0].value !== s.label) {
            clearErrors(`customLinks.${i}.value` as keyof CreateAtiraLinkDto);
            clearErrors(`customLinks.${i}.label` as keyof CreateAtiraLinkDto);
          }
        });
      }
    });

    onScrollToFirstError();
  };

  const onSave = async () => {
    try {
      setLoading?.(true);

      if (editMode) {
        await edit();
      } else {
        await create();
        localStorage.removeItem('selectedThemeFromTemplates');
      }

      await dispatch(
        AtiraLinkActions.getUserAtiraLinks({ userId: user._id }),
      ).unwrap();

      reset(watch(), {
        keepValues: false,
        keepDirty: false,
        keepDefaultValues: false,
      });

      AtiraToast.success(
        t(editMode ? 'atira_link.edit.success' : 'atira_link.create.success'),
      );

      if (!editMode) {
        navigate('/dashboard');
      }
    } catch (e: any) {
      onSetErrors(e);

      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setLoading?.(false);
    }
  };

  const create = async () => {
    const dto = values as CreateAtiraLinkDto;

    if (user.plan?.perks.embed !== AtiraLinkPlanEmbed.PLAN_2) {
      delete dto?.embed;
    }

    dto.socials = dto.socials?.map((s, i) => ({
      ...s,
      order: i,
    }));

    const link = await dispatch(
      AtiraLinkActions.createAtiraLink({ ...dto, userId: user._id }),
    ).unwrap();
    dispatch(AtiraLinkActions.setSelectedAtiraLink(link));
  };

  const edit = async () => {
    const dto = values as EditAtiraLinkDto;

    const userPlan =
      user.plan?.freeTrial?.freeTrialPlan ||
      user.plan?.unpaidPlan?.unpaidPlanType ||
      user.plan?.type;
    const canEditLinkName = userPlan === AtiraLinkPlanType.PLAN_2;

    if (!(canEditLinkName && dto.linkName !== selectedAtiraLink?.linkName)) {
      delete dto.linkName;
    }

    if (user.plan?.perks.embed !== AtiraLinkPlanEmbed.PLAN_2) {
      delete dto?.embed;
    }

    dto.socials = dto.socials?.map((s, i) => ({
      ...s,
      order: i,
    }));

    if (user)
      return dispatch(
        AtiraLinkActions.editAtiraLink({
          ...dto,
          atiraLinkId: selectedAtiraLink!._id,
          userId: user._id,
        } as EditAtiraLinkDto),
      ).unwrap();
  };

  const onPreview = () => {
    const userLinkURL = getUserLinkUrl(selectedAtiraLink?.linkName!);

    window.open(userLinkURL, '_blank');
  };

  return (
    <Container isPreviewMode={isPreviewMode} previewMode={previewMode}>
      <PhoneWrapper isPreviewMode={isPreviewMode} previewMode={previewMode}>
        <PhoneFrameWrapper
          previewMode={previewMode}
          isPreviewMode={isPreviewMode}
          customBackgroundColor={values.styles?.background?.color}
        >
          <AtiraLinkPreviewPhoneEJS
            isPreviewMode={isPreviewMode}
            atiraLink={values}
            currentLinkLang={currentLinkLang}
            previewMode={previewMode}
          />
        </PhoneFrameWrapper>
      </PhoneWrapper>

      {showActions ? (
        <ButtonsWrapper>
          <Tooltip title={t(`common.${editMode ? 'update' : 'save'}`)}>
            <StyledButton
              disabled={
                (values.title?.length || 0) < 3 || values.linkName?.length < 3
              }
              onClick={handleSubmit(onSave, onSetErrors)}
              loading={loading}
              dataTestId={atiraLinkTestIDs.phone.update}
              icon={faFloppyDisk}
              width="7rem"
            >
              {t(`common.${editMode ? 'update' : 'save'}`)}
            </StyledButton>
          </Tooltip>

          <StyledButton
            onClick={onPreview}
            disabled={!values.linkName || !editMode}
            dataTestId={atiraLinkTestIDs.phone.preview}
            icon={faEye}
            width="7rem"
          >
            {t('common.preview')}
          </StyledButton>
        </ButtonsWrapper>
      ) : null}
    </Container>
  );
};
